import { memo, useState } from 'react';
import { TabNav, Tab } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import ThreadTab from './ThreadTab.jsx';
import AttestationTab from './AttestationTab.jsx';
import AuditTrailTab from './AuditTrailTab.jsx';

const TABS = { THREAD: 'thread', ATTESTATION: 'attestation', AUDIT: 'audit' };

/** HTL V1 panel tabs: 13px-ish type, tight x-pad, white active label + blue underline. */
const PANEL_TAB_CLASS = '!px-1 !py-2 text-[13px] leading-none !font-semibold';

const PANEL_TAB_SELECTED_CLASS = '!text-content dark:!text-white/80';

/**
 * Thread / Attestation / Audit Trail.
 *
 * `layout="panel"` — TabNav pinned above the content. Thread keeps a full-height
 * side card (composer docked). Attestation / Audit use content-sized cards inside
 * a scrolling column (`scrollbar-aimsos-auto-sm`) — not a stretched full-height shell.
 */
function EventTabs({
  thread,
  attestation = [],
  auditTrail,
  eventId,
  event = null,
  layout = 'stack',
  hideAttestation = false,
  loading = false,
  error = null,
}) {
  const { t } = useTranslation('work-queues-app');
  const [active, setActive] = useState(TABS.THREAD);
  const isPanel = layout === 'panel';
  const visibleActive = hideAttestation && active === TABS.ATTESTATION ? TABS.THREAD : active;
  const isThread = visibleActive === TABS.THREAD;

  const tabClass = (selected) =>
    [isPanel ? PANEL_TAB_CLASS : '', isPanel && selected ? PANEL_TAB_SELECTED_CLASS : '']
      .filter(Boolean)
      .join(' ');

  return (
    <div className={isPanel ? 'flex min-h-0 w-full flex-1 flex-col overflow-hidden' : 'flex flex-col gap-4'}>
      <div className={isPanel ? 'shrink-0 border-b border-edge pt-3' : undefined}>
        <TabNav ariaLabel={t('detail.panelTabs')} className={isPanel ? '!gap-5' : undefined}>
          <Tab
            size="sm"
            selected={visibleActive === TABS.THREAD}
            onClick={() => setActive(TABS.THREAD)}
            className={tabClass(visibleActive === TABS.THREAD)}
          >
            {t('detail.tabThread')}
          </Tab>
          {!hideAttestation ? (
            <Tab
              size="sm"
              selected={visibleActive === TABS.ATTESTATION}
              onClick={() => setActive(TABS.ATTESTATION)}
              className={tabClass(visibleActive === TABS.ATTESTATION)}
            >
              {t('detail.tabAttestation')}
            </Tab>
          ) : null}
          <Tab
            size="sm"
            selected={visibleActive === TABS.AUDIT}
            onClick={() => setActive(TABS.AUDIT)}
            className={tabClass(visibleActive === TABS.AUDIT)}
          >
            {t('detail.tabAuditTrail')}
          </Tab>
        </TabNav>
      </div>

      {isThread ? (
        <div
          className={
            isPanel
              ? 'mt-3 flex min-h-0 flex-1 flex-col overflow-hidden rounded-[10px] border border-edge bg-surface'
              : undefined
          }
        >
          <ThreadTab
            thread={thread}
            eventId={eventId}
            event={event}
            layout={layout}
            loading={loading}
            error={error}
          />
        </div>
      ) : (
        <div
          className={
            isPanel
              ? 'mt-3 min-h-0 flex-1 overflow-y-auto scrollbar-aimsos-auto-sm'
              : undefined
          }
        >
          {visibleActive === TABS.ATTESTATION && (
            <AttestationTab entries={attestation} event={event} />
          )}
          {visibleActive === TABS.AUDIT && (
            <AuditTrailTab entries={auditTrail} loading={loading} error={error} />
          )}
        </div>
      )}
    </div>
  );
}

// Memoized for the same reason as EventDetailBody (CT-5308): the resizable
// inbox list panel's own drag state re-renders its sibling Panel, and this
// tree (thread/attestation/audit) is the heaviest one in it.
export default memo(EventTabs);
